import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { Box, CircularProgress } from "@mui/material";
import { organizationApi } from "../services/organization-service";
import { customizationApi } from "../services/customization-service";
import { defaultStoredTheme, setStoredTheme } from "@/utils/theme-context";
import {
  needsPlatformTabBranding,
  resolveTabBranding,
} from "@/utils/theme-branding";
import { cacheApi } from "@/services/cache-service";
import { useAuth } from "./AuthContext";

const OrganizationContext = createContext();
const THEME_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider",
    );
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [themeLoading, setThemeLoading] = useState(true);
  const organizationsLoadPromiseRef = useRef(null);
  const lastThemeFetchRef = useRef(0);
  const themeRefreshInProgressRef = useRef(false);
  const { currentUser, checkInferenceOnlyStatus, setInferenceOnly } = useAuth();

  // Load organizations from API - only when explicitly called
  const loadOrganizations = useCallback(async () => {
    // React StrictMode runs bootstrap effects twice in development. Reuse the
    // first request so the second pass does not treat "already loading" as a
    // failed organization load and briefly apply the platform default theme.
    if (organizationsLoadPromiseRef.current) {
      return organizationsLoadPromiseRef.current;
    }

    const loadPromise = (async () => {
      // Set loading immediately to prevent race conditions
      setLoading(true);
      setError(null);

      try {
        const response = await organizationApi.getOrganizations();
        setOrganizations(response.data);

        // Load current organization from localStorage only on first load
        if (!initialized) {
          const savedOrgId = localStorage.getItem("currentOrganizationId");
          if (savedOrgId) {
            const savedOrg = response.data.find(
              (org) => org.id === savedOrgId || org.id.toString() === savedOrgId,
            );
            if (savedOrg) {
              const { data: organizationDetails } =
                await organizationApi.getOrganization(savedOrg.id);
              setCurrentOrganization(organizationDetails);
            } else {
              // If saved organization doesn't exist anymore, clear it
              // localStorage.removeItem("currentOrganizationId");
              setOrganization(null);
            }
          }
          setInitialized(true);
        }
        return true;
      } catch (err) {
        console.error("Error loading organizations:", err);
        setError("Failed to load organizations");
        return false;
      } finally {
        setLoading(false);
      }
    })();

    organizationsLoadPromiseRef.current = loadPromise;

    try {
      return await loadPromise;
    } finally {
      if (organizationsLoadPromiseRef.current === loadPromise) {
        organizationsLoadPromiseRef.current = null;
      }
    }
  }, [initialized]);

  const updatePreferences = async (last_active_organization_id) => {
    try {
      setLoading(true);
      await cacheApi.updatePreferences({
        last_active_organization_id,
      });
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to update user preferences",
      );
    } finally {
      setLoading(false);
    }
  };

  const setOrganizationInferenceById = async (organization_id) => {
    const { data } = await organizationApi.getOrganization(organization_id);
    setInferenceOnly(data?.is_inference_only ?? false);
  };
  // Set current organization
  const setOrganization = useCallback(async (organization) => {
    if (organization) {
      const { data: organizationDetails } =
        await organizationApi.getOrganization(organization.id);
      updatePreferences(organizationDetails.id);
      setCurrentOrganization(organizationDetails);
      localStorage.setItem("currentOrganizationId", organizationDetails.id);
      localStorage.setItem("currentOrganizationName", organizationDetails.name);
      localStorage.setItem("currentOrganizationSlug", organizationDetails.slug);
      localStorage.setItem(
        "currentOrganizationIsInferenceOnly",
        String(organizationDetails?.is_inference_only === true),
      );
    } else {
      updatePreferences(null);
      setCurrentOrganization(null);
      localStorage.removeItem("currentOrganizationId");
      localStorage.removeItem("currentOrganizationName");
      localStorage.removeItem("currentOrganizationSlug");
      localStorage.removeItem("currentOrganizationIsInferenceOnly");
    }
  }, []);

  // Create new organization
  const createOrganization = useCallback(async (organizationData) => {
    try {
      const response =
        await organizationApi.createOrganization(organizationData);
      const newOrg = response.data;
      setOrganizations((prev) => [...prev, newOrg]);
      return newOrg;
    } catch (err) {
      console.error("Error creating organization:", err);
      throw err;
    }
  }, []);

  // Update organization
  const updateOrganization = useCallback(
    async (orgId, organizationData) => {
      try {
        const response = await organizationApi.updateOrganization(
          orgId,
          organizationData,
        );
        const updatedOrg = response.data;
        setOrganizations((prev) =>
          prev.map((org) => (org.id === orgId ? updatedOrg : org)),
        );

        // Update current organization if it's the one being updated
        if (currentOrganization && currentOrganization.id === orgId) {
          setCurrentOrganization(updatedOrg);
        }

        return updatedOrg;
      } catch (err) {
        console.error("Error updating organization:", err);
        throw err;
      }
    },
    [currentOrganization],
  );

  const updateOrganizationLanguage = useCallback((orgId, language) => {
    setOrganizations((prev) =>
      prev.map((org) =>
        String(org.id) === String(orgId)
          ? { ...org, default_language: language, language }
          : org,
      ),
    );

    setCurrentOrganization((prev) =>
      prev && String(prev.id) === String(orgId)
        ? { ...prev, default_language: language, language }
        : prev,
    );
  }, []);

  // Delete organization
  const deleteOrganization = useCallback(
    async (orgId) => {
      try {
        await organizationApi.deleteOrganization(orgId);
        setOrganizations((prev) => prev.filter((org) => org.id !== orgId));

        // If current organization is deleted, clear it
        if (currentOrganization && currentOrganization.id === orgId) {
          // setCurrentOrganization(null);
          setOrganization(null);
          // localStorage.removeItem("currentOrganizationId");
        }
      } catch (err) {
        console.error("Error deleting organization:", err);
        throw err;
      }
    },
    [currentOrganization],
  );

  // Leave organization
  const leaveOrganization = useCallback(
    async (orgId) => {
      try {
        await organizationApi.leaveOrganization(orgId);
        setOrganizations((prev) => prev.filter((org) => org.id !== orgId));

        // If current organization is the one being left, clear it
        if (currentOrganization && currentOrganization.id === orgId) {
          setCurrentOrganization(null);
          setOrganization(null);
          // localStorage.removeItem("currentOrganizationId");
        }
      } catch (err) {
        console.error("Error leaving organization:", err);
        throw err;
      }
    },
    [currentOrganization],
  );

  // Get organization header for API requests
  const getOrganizationHeader = useCallback(() => {
    return currentOrganization
      ? { "X-Organization-ID": currentOrganization.id }
      : {};
  }, [currentOrganization]);

  const getTheme = async ({ preserveCurrentTheme = false } = {}) => {
    try {
      if (!preserveCurrentTheme) setError(null);
      const { data } = await customizationApi.getTheme(currentOrganization?.id);
      if (data?.theme) {
        let resolvedTheme = data.theme;

        if (needsPlatformTabBranding(resolvedTheme)) {
          let platformTheme = defaultStoredTheme;

          try {
            const { data: defaultThemeData } =
              await customizationApi.getDefaultTheme();
            platformTheme = defaultThemeData?.theme || defaultStoredTheme;
          } catch {
            // Keep the frontend defaults when platform branding is unavailable.
          }

          resolvedTheme = resolveTabBranding(
            resolvedTheme,
            platformTheme,
            defaultStoredTheme,
          );
        }

        setStoredTheme(resolvedTheme);
        return true;
      }
      return await getDefaultTheme({ preserveCurrentTheme });
    } catch (err) {
      // 404 means no custom theme exists yet — fall back to defaults silently
      if (err?.response?.status === 404) {
        return await getDefaultTheme({ preserveCurrentTheme });
      }
      if (!preserveCurrentTheme) {
        setError(
          err?.response?.data?.detail || err.message || "Failed to load theme",
        );
      }
      return false;
    }
  };

  const getDefaultTheme = async ({ preserveCurrentTheme = false } = {}) => {
    try {
      const { data } = await customizationApi.getDefaultTheme();
      setStoredTheme(data?.theme || defaultStoredTheme);
      return true;
    } catch (err) {
      if (!preserveCurrentTheme) {
        setStoredTheme(defaultStoredTheme);
        setError(
          err?.response?.data?.detail || err.message || "Failed to load theme",
        );
      }
      return false;
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setThemeLoading(false);
      return;
    }

    let isCurrent = true;

    const loadTheme = async () => {
      setThemeLoading(true);

      let themeLoaded = false;

      if (currentOrganization?.id) {
        themeLoaded = await getTheme();
      } else if (
        initialized ||
        !localStorage.getItem("currentOrganizationId")
      ) {
        themeLoaded = await getDefaultTheme();
      } else {
        const organizationsLoaded = await loadOrganizations();
        if (!organizationsLoaded) {
          await getDefaultTheme();
          if (isCurrent) setThemeLoading(false);
        }
        return;
      }

      if (themeLoaded) lastThemeFetchRef.current = Date.now();
      if (isCurrent) setThemeLoading(false);
    };

    loadTheme();

    if (currentOrganization?.id) {
      setOrganization(currentOrganization);
    }

    return () => {
      isCurrent = false;
    };
  }, [currentUser?.id, currentOrganization?.id, initialized]);

  useEffect(() => {
    if (!currentUser) return;

    const refreshThemeIfStale = async () => {
      if (document.visibilityState !== "visible") return;
      if (
        Date.now() - lastThemeFetchRef.current <
          THEME_REFRESH_INTERVAL_MS ||
        themeRefreshInProgressRef.current
      ) {
        return;
      }

      themeRefreshInProgressRef.current = true;
      try {
        const themeLoaded = currentOrganization?.id
          ? await getTheme({ preserveCurrentTheme: true })
          : await getDefaultTheme({ preserveCurrentTheme: true });

        if (themeLoaded) lastThemeFetchRef.current = Date.now();
      } finally {
        themeRefreshInProgressRef.current = false;
      }
    };

    const intervalId = window.setInterval(
      refreshThemeIfStale,
      THEME_REFRESH_INTERVAL_MS,
    );
    document.addEventListener("visibilitychange", refreshThemeIfStale);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshThemeIfStale);
    };
  }, [currentUser?.id, currentOrganization?.id]);

  useEffect(() => {
    if (!currentUser) return;

    if (!currentOrganization) {
      checkInferenceOnlyStatus(currentUser);
    } else {
      setOrganizationInferenceById(currentOrganization.id);
    }
  }, [currentUser, currentOrganization]);

  const value = {
    currentOrganization,
    organizations,
    loading,
    error,
    initialized,
    setOrganization,
    createOrganization,
    updateOrganization,
    updateOrganizationLanguage,
    deleteOrganization,
    leaveOrganization,
    loadOrganizations,
    getOrganizationHeader,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {currentUser && themeLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        children
      )}
    </OrganizationContext.Provider>
  );
};
